import { Reducer } from 'redux'

import { SocketState, SocketActionTypes, WebSocketConnectionStatus } from './types'
import { FOLLOW_AGENT } from 'common/api/storageConsts'

export const readFollowAgent = (): boolean => {
  try {
    return localStorage.getItem(FOLLOW_AGENT) !== 'false'
  } catch {
    return true
  }
}

export const initialState: SocketState = {
  previousWebSocketConnectionStatus: WebSocketConnectionStatus.ON,
  webSocketConnectionStatus: WebSocketConnectionStatus.ON,
  progress: { show: false, value: 0 },
  instanceResources: undefined,
  uploader: undefined,
  appIsLocked: false,
  pendingOperations: new Map(),
  lastOperationResult: undefined,
  operationProgress: undefined,
  persistentOperation: undefined,
  agentPresence: undefined,
  followAgent: readFollowAgent()
}

const reducer: Reducer<SocketState> = (
  state: SocketState = initialState,
  action: { type: SocketActionTypes; payload?: any }
) => {
  switch (action.type) {
    case SocketActionTypes.WEBSOCKET_CONNECTION_STATUS_OFF: {
      return { ...state, webSocketConnectionStatus: WebSocketConnectionStatus.OFF }
    }
    case SocketActionTypes.WEBSOCKET_CONNECTION_STATUS_ON: {
      return { ...state, webSocketConnectionStatus: WebSocketConnectionStatus.ON }
    }
    case SocketActionTypes.SET_WEBSOCKET_PREVIOUS_CONNECTION_STATUS: {
      return { ...state, previousWebSocketConnectionStatus: action.payload }
    }
    case SocketActionTypes.SET_PROGRESS: {
      return { ...state, progress: { ...state.progress, ...action.payload } }
    }
    case SocketActionTypes.SET_PROGRESSBAR: {
      return { ...state, progressbar: { ...state.progressbar, ...action.payload } }
    }
    case SocketActionTypes.HIDE_PROGRESSBAR: {
      return { ...state, progressbar: undefined }
    }
    case SocketActionTypes.UPDATE_INSTANCE_RESOURCES: {
      return { ...state, instanceResources: action.payload }
    }
    case SocketActionTypes.SET_UPLOADER_PARAMS: {
      return { ...state, uploader: action.payload }
    }
    case SocketActionTypes.INTERNAL_TESTS_STATUS: {
      return { ...state, internalTests: action.payload }
    }
    case SocketActionTypes.RESET_INTERNAL_TESTS: {
      return { ...state, internalTests: undefined }
    }
    case SocketActionTypes.SET_APP_LOCK_INFO: {
      return { ...state, appIsLocked: action.payload }
    }
    case SocketActionTypes.SET_AGENT_PRESENCE: {
      return { ...state, agentPresence: action.payload }
    }
    case SocketActionTypes.SET_FOLLOW_AGENT: {
      return { ...state, followAgent: action.payload }
    }
    case SocketActionTypes.SET_FORCE_STOP_PHASE: {
      return { ...state, forceStopPhase: action.payload }
    }
    case SocketActionTypes.SET_REFRESH_APP_NOTIFICATIONS: {
      return { ...state, refreshAppNotifications: action.payload }
    }
    case SocketActionTypes.SET_SCHEDULED_TASK_UPDATE: {
      // Counter instead of a timestamp: two events for the same task can land
      // in the same millisecond, and subscribers react to `seq` changing.
      const seq = (state.scheduledTaskUpdate?.seq ?? 0) + 1
      return { ...state, scheduledTaskUpdate: { ...action.payload, seq } }
    }
    case SocketActionTypes.TRANSLATIONS_PROGRESS: {
      return { ...state, translationsProgress: action.payload }
    }
    case SocketActionTypes.RESET_TRANSLATIONS_PROGRESS: {
      return { ...state, translationsProgress: undefined }
    }
    case SocketActionTypes.OPEN_APP_WITH_UNCONFIRMED_CHANGES: {
      return { ...state, openAppWithUnconfirmedChanges: action.payload }
    }
    case SocketActionTypes.ADD_PENDING_FILE_OPERATION: {
      const newPendingOperations = new Map(state.pendingOperations)
      newPendingOperations.set(action.payload.operationId, action.payload)
      return {
        ...state,
        pendingOperations: newPendingOperations
      }
    }
    case SocketActionTypes.REMOVE_PENDING_FILE_OPERATION: {
      const newPendingOperations = new Map(state.pendingOperations)
      newPendingOperations.delete(action.payload)
      return {
        ...state,
        pendingOperations: newPendingOperations
      }
    }
    case SocketActionTypes.SET_FILE_OPERATION_RESULT: {
      return {
        ...state,
        lastOperationResult: action.payload
      }
    }
    case SocketActionTypes.CLEAR_FILE_OPERATION_RESULT: {
      return {
        ...state,
        lastOperationResult: undefined
      }
    }
    case SocketActionTypes.SET_ASYNC_OPERATION_PROGRESS: {
      return {
        ...state,
        operationProgress: action.payload
      }
    }
    case SocketActionTypes.CLEAR_ASYNC_OPERATION_PROGRESS: {
      return {
        ...state,
        operationProgress: undefined
      }
    }
    case SocketActionTypes.SET_PERSISTENT_OPERATION: {
      return {
        ...state,
        persistentOperation: action.payload
      }
    }
    case SocketActionTypes.CLEAR_PERSISTENT_OPERATION: {
      return {
        ...state,
        persistentOperation: undefined
      }
    }
    default: {
      return state
    }
  }
}

export { reducer as socketReducer }
